import { messages, TwilioVerificationChannelEnum, useTwilioSendCodeMutation } from '@alluradirect/react';
import { SendCodeForm } from 'components/views/verification/components/SendCodeForm';
import { VerificationStepEnum } from 'components/views/verification/VerificationDialog';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Turnstile from 'components/layout/Page/components/Turnstile';
import { verifyTurnstileToken } from 'components/views/pages/_general/helpers';

export interface SendCodeValues {
  data: string;
  is_taken: boolean;
  is_call: boolean;
}

const emailValidation = yup.object().shape({
  data: yup
    .string()
    .required(messages.requiredValue)
    .min(3, 'Email must be at least 3 characters')
    .email('Please enter a valid email'),
  is_taken: yup
    .boolean()
    .required(messages.requiredValue)
    .test('is false', 'email taken by another user', (value: any) => value !== true),
});

const phoneValidation = yup.object().shape({
  data: yup.string().required(messages.requiredValue).min(5, 'Phone number must be at least 5 characters'),
  is_taken: yup
    .boolean()
    .required(messages.requiredValue)
    .test(
      'is false',
      'The phone number you have used is already being used by another alluraDirect account.',
      (value: any) => value !== true,
    ),
});

export const SendCodeFormWrapper = ({
  data,
  channel,
  setStep,
  handleCloseDialog,
  setData,
}: {
  data: string;
  channel: TwilioVerificationChannelEnum.sms | TwilioVerificationChannelEnum.email;
  handleCloseDialog: () => void;
  setStep: React.Dispatch<React.SetStateAction<VerificationStepEnum>>;
  setData: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const sendCode = useTwilioSendCodeMutation();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const validationSchema = useMemo(() => {
    if (channel === TwilioVerificationChannelEnum.sms) {
      return phoneValidation;
    } else if (channel === TwilioVerificationChannelEnum.email) {
      return emailValidation;
    } else {
      return `Channel: ${channel} not implemented`;
    }
  }, [channel]);

  return (
    <Formik
      initialValues={{ data, is_taken: false } as SendCodeValues}
      initialTouched={{
        is_taken: true,
      }}
      validationSchema={validationSchema}
      onSubmit={async ({ data, is_call }, { setSubmitting }) => {
        if (!captchaToken) {
          toast.error("Please complete the safety check.");
          setSubmitting(false);
          return;
        }

        try {
          const widgetName = channel === TwilioVerificationChannelEnum.sms ? 'sendcodesms' : 'sendcodeemail';
          const turnstileData = await verifyTurnstileToken(captchaToken, widgetName);
          if (!turnstileData?.SUCCESS || !turnstileData?.DATA?.SUCCESS) {
            toast.error("Server rejected the request or captcha failed.");
            setSubmitting(false);
            return;
          }
        } catch (e) {
          toast.error("Error connecting to the backend server.");
          setSubmitting(false);
          return;
        }

        sendCode.mutate(
          { value: data, channel: is_call ? TwilioVerificationChannelEnum.call : channel },
          {
            onSuccess: () => {
              setData(data);
              setStep(VerificationStepEnum.VerifyCode);
            },
            onSettled: () => {
              setSubmitting(false);
            },
          },
        );
      }}
    >
      <Form>
        <SendCodeForm channel={channel} handleCloseDialog={handleCloseDialog} isCaptchaValid={!!captchaToken}>
          <Turnstile
            onVerify={(token) => setCaptchaToken(token)}
            siteKey={
              channel === TwilioVerificationChannelEnum.sms
                ? process.env.NEXT_PUBLIC_TURNSTILE_SENDCODE_SMS_KEY ?? ''
                : process.env.NEXT_PUBLIC_TURNSTILE_SENDCODE_EMAIL_KEY ?? ''
            }
          />
        </SendCodeForm>
      </Form>
    </Formik>
  );
};
